import { IShoppingCartActionTypes } from "action/interfaces/shopping-cart.interface";
import { AddressSummary, CreditCardSummary, PickupInfo } from "api/interfaces/checkout.type";
import {
  IAdditionalCharges,
  IOrderedItemView, IShoppingCartV3,
} from "interfaces/session/ShoppingCart";
import { ItemInCart } from "interfaces/session/user-session-v3";
import { DELIVERY_METHODS } from "../constant/action-constants";

export interface IShoppingCartStateReducer extends IShoppingCartV3 {

  items: ItemInCart[],
  hasRefreshed: boolean,
  paymentMethod: string | null,
  deliveryMethod: string;
  postalCode: string;
  selectedShipment: number,

  showPickupAvailabilityAlert: boolean,
  showDeliveryAvailabilityAlert: boolean,

  itemsNotAvailableForPickup: IOrderedItemView[],
  itemsNotAvailableForDelivery: IOrderedItemView[],
  selectedAddress: AddressSummary | null;
  pickupInfo: PickupInfo | null;
  lastUsedAddressAndCard: boolean;
  selectedCard: CreditCardSummary | null;
  additionalCharges: IAdditionalCharges[];
  enableBusinessCheckout: boolean;
  selectedBusinessCredits: number;
  availableDeliveryMethodsForProducts: string[];
  eligibleForStripeExpressCheckout: boolean;
  selectedLoyaltyPoints: number | null;
  loyaltyPointsBalance: number | null;
  cartLoyaltyValue: number | null;
  entireOrderCoveredByLoyaltyPoints: boolean;
}

export const initialState: IShoppingCartStateReducer = {
  availableDeliveryMethodsDetails: [],
  ipLocation: true,
  numberOfItems: 0,
  outOfStockItems: [],
  pickUpLocations: [],
  province: "ON",
  selectedShipment: 0,
  selectedShipmentOption: "GROUND",
  shipmentOptions: [],
  subTotal: 0,
  tax: 0,
  environmentalLevy: 0,
  paymentMethod: null,
  totalCost: 0,
  selectedCredits: 0,
  selectedBusinessCredits: 0,
  shippingCostBreakDown: {
    baseShippingCost: 0,
    poBoxCost: 0,
    remoteAreaSurchargeCost: 0,
  },
  subtotalWithDiscountDetails: {
    appliedDiscountsCost: 0,
    subtotalWithDiscount: 0,
    discountsWithType: [],
  },
  taxStatement: "",
  postalCode: "",
  availableItems: [],
  deliveryMethod: "SHIPMENT",
  savedItems: [],
  hasRefreshed: false,
  showPickupAvailabilityAlert: false,
  // showPickupItemQuantityChangeAlert: false,
  showDeliveryAvailabilityAlert: false,
  // showDeliveryItemQuantityChangeAlert: false,
  itemsNotAvailableForPickup: [],
  itemsNotAvailableForDelivery: [],
  items: [],
  selectedDeliveryMethod: "",
  // remove this and use postalCode
  zipCode: "",
  shipmentCost: 0,
  amountRequiredForFreeShipping: 0,
  validPostalCodeForProgressBar: false,
  selectedAddress: null,
  pickupInfo: null,
  lastUsedAddressAndCard: false,
  selectedCard: null,
  additionalCharges: [],
  savingsBreakDown: [],
  enableBusinessCheckout: true,
  availableDeliveryMethodsForProducts: [],
  eligibleForStripeExpressCheckout: false,
  selectedLoyaltyPoints: null,
  loyaltyPointsBalance: null,
  cartLoyaltyValue: null,
  entireOrderCoveredByLoyaltyPoints: false,
};

const shoppingCartReducer = (state = initialState, action: IShoppingCartActionTypes): IShoppingCartStateReducer => {
  switch (action.type) {
  case "ADD_ITEM":
    return {
      ...state,
      availableItems: action.data,
    };

  case "SET_PAYMENT_METHOD":
    return {
      ...state,
      paymentMethod: action.data,
    };

  case "SET_SHOPPING_CART": // user-session-v3 -> shoppingCartState
    return {
      ...state,
      // hasRefreshed: true,
      ...action.data,
    };

  case "REFRESH_SHOPPING_CART": { // get-shopping-cart-v3
    const { data } = action;
    const itemsNotAvailableForPickup: IOrderedItemView[] = [];
    const itemsNotAvailableForDelivery: IOrderedItemView[] = [];

    if (data.availableItems && data.availableItems.length) {
      data.availableItems.forEach((item: any) => {
        if (!item.availableDeliveryMethods.find((key: string) => key === DELIVERY_METHODS.PICKUP)) itemsNotAvailableForPickup.push(item);
        if (!item.availableDeliveryMethods.find((key: string) => key === DELIVERY_METHODS.DELIVERY)) itemsNotAvailableForDelivery.push(item);
        // itemsNotAvailableForShiping
      });
    }
    const showPickupAvailabilityAlert = (data.selectedDeliveryMethod === DELIVERY_METHODS.PICKUP) && (itemsNotAvailableForPickup.length > 0);
    const showDeliveryAvailabilityAlert = (data.selectedDeliveryMethod === DELIVERY_METHODS.DELIVERY) && (itemsNotAvailableForDelivery.length > 0);
    const selectedShipment = data.selectedDeliveryMethod === DELIVERY_METHODS.SHIPPING && data.shipmentOptions && data.selectedShipmentOption
      ? data.shipmentOptions.findIndex((item: any) => item.serviceName === data.selectedShipmentOption) : 0;

    const productAvailableDeliveryMethods = data.availableItems.flatMap((item) => item.availableDeliveryMethods);
    const uniqueProductDeliveryMethods = [...new Set(productAvailableDeliveryMethods)];

    return {
      ...state,
      ...data,
      // selectedDeliveryMethod
      // zipCode
      // errorMessage
      // amountRequiredForFreeShipping
      showPickupAvailabilityAlert,
      showDeliveryAvailabilityAlert,
      itemsNotAvailableForPickup,
      itemsNotAvailableForDelivery,
      deliveryMethod: data.selectedDeliveryMethod,
      // TODO: remove items and use shopping cart availableItems
      // items: data.availableItems,
      selectedShipment,
      hasRefreshed: true,
      postalCode: data.zipCode,
      availableDeliveryMethodsForProducts: uniqueProductDeliveryMethods,
    };
  }

  case "SET_ORDER_SHIPPING_COST": {
    return {
      ...state,
      ...action.data,
    };
  }

  case "SET_SELECTED_ADDRESS": {
    return {
      ...state,
      ...action.data,
    };
  }

  case "SET_BUSINESS_CHECKOUT_STATUS": {
    return {
      ...state,
      enableBusinessCheckout: action.data,
    };
  }

  case "SET_SELECTED_BUSINESS_CREDITS": {
    return {
      ...state,
      selectedBusinessCredits: action.data,
    };
  }

  case "SET_LAST_USED_ADDRESS_AND_CARD": {
    return {
      ...state,
      ...action.data,
    };
  }

  case "SET_SELECTED_CARD": {
    return {
      ...state,
      ...action.data,
    };
  }

  case "SET_PICKUP_INFO": {
    return {
      ...state,
      pickupInfo: action.data,
    };
  }

  case "SET_CART_LOYALTY": {
    return {
      ...state,
      cartLoyaltyValue: action.data.cartLoyaltyValue || null,
      loyaltyPointsBalance: action.data.loyaltyPointsBalance || null,
      selectedLoyaltyPoints: action.data.selectedLoyaltyPoints || null,
      entireOrderCoveredByLoyaltyPoints: action.data.entireOrderCoveredByLoyaltyPoints || false,
    };
  }

  default:
    return state;
  }
};

export default shoppingCartReducer;
